/**
 * FileResource: owner vs creator history (BA transfer / deleted uploader).
 * Prefer original_creator_id + name lookup; then original_ba_full_name; else "Deleted user".
 * uploader_id may be null — do not rely on it for display.
 */

export const DELETED_USER_LABEL = 'Deleted user'

export function getRawFile(file) {
  if (!file) return null
  return file.file || file
}

function pickField(file, key) {
  if (!file) return undefined
  const inner = file.file
  if (inner && Object.prototype.hasOwnProperty.call(inner, key)) {
    return inner[key]
  }
  if (Object.prototype.hasOwnProperty.call(file, key)) {
    return file[key]
  }
  return undefined
}

export function getOriginalBaFullName(file) {
  const v = pickField(file, 'original_ba_full_name')
  const s = v != null ? String(v).trim() : ''
  return s
}

export function getOriginalCreatorId(file) {
  const v = pickField(file, 'original_creator_id')
  if (v === undefined || v === null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export function getOriginalOwnerId(file) {
  const v = pickField(file, 'original_owner_id')
  if (v === undefined || v === null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** May be null after BA/uploader deletion — do not assume present. */
export function getUploaderId(file) {
  const v = pickField(file, 'uploader_id')
  if (v === undefined || v === null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** Same placeholder as `status_reason` in tradie file tables when unknown. */
export const FILE_VISIBILITY_PLACEHOLDER = '-'

/** Public / Private for list and table cells, or `-` when unknown (matches other columns). */
export function formatFileVisibilityDisplay(row) {
  if (!row) return FILE_VISIBILITY_PLACEHOLDER
  const raw = pickField(row, 'visibility')
  if (raw == null || raw === '') return FILE_VISIBILITY_PLACEHOLDER
  const v = String(raw).toLowerCase().trim()
  if (v === 'public') return 'Public'
  if (v === 'private') return 'Private'
  return String(raw)
}

export function getOwnedByLabel(file) {
  const u = (file && file.user) || (file && file.file && file.file.user)
  if (!u) return ''
  if (u.mine) return 'You'
  if (file && file.shared_by) return file.shared_by.full_name || ''
  return u.full_name || u.email || ''
}

/**
 * @param {object|null|undefined} file
 * @param {function(number): string|null|undefined} [resolveUserNameById] — id from original_creator_id
 */
export function getCreatedByLabel(file, resolveUserNameById) {
  if (!file) return DELETED_USER_LABEL

  const creatorId = getOriginalCreatorId(file)
  const baName = getOriginalBaFullName(file)

  if (creatorId != null) {
    if (typeof resolveUserNameById === 'function') {
      const resolved = resolveUserNameById(creatorId)
      if (resolved && String(resolved).trim()) {
        return String(resolved).trim()
      }
    }
    const u = file.user || (file.file && file.file.user)
    if (u && Number(u.id) === Number(creatorId)) {
      const name = (u.full_name || u.email || '').trim()
      if (name) return name
    }
  }

  if (baName) return baName

  if (creatorId != null) {
    return DELETED_USER_LABEL
  }

  const owner = getOwnedByLabel(file)
  if (owner) return owner
  return DELETED_USER_LABEL
}

/**
 * @param {function(number): string|null|undefined} [resolveUserNameById]
 */
export function isSameCreatorAndOwner(file, resolveUserNameById) {
  const ownerU = (file && file.user) || (file && file.file && file.file.user)
  const ownerId =
    ownerU && ownerU.id != null && ownerU.id !== '' ? Number(ownerU.id) : null
  const creatorId = getOriginalCreatorId(file)

  if (
    ownerId != null &&
    Number.isFinite(ownerId) &&
    creatorId != null &&
    ownerId === creatorId
  ) {
    return true
  }

  if (creatorId != null && ownerId != null && Number.isFinite(ownerId)) {
    return false
  }

  const c = getCreatedByLabel(file, resolveUserNameById)
  const o = getOwnedByLabel(file)
  return Boolean(c && o && c === o)
}

/** Index user names from a list of files (e.g. file grid) for creator id resolution. */
export function buildCreatorNameResolverFromFiles(files) {
  const map = new Map()
  const add = (u) => {
    if (!u || u.id == null) return
    const name = (u.full_name || u.email || '').trim()
    if (name) map.set(Number(u.id), name)
  }
  for (const f of files || []) {
    if (!f) continue
    add(f.user)
    if (f.file) add(f.file.user)
    if (f.shared_by) add(f.shared_by)
  }
  return (id) => map.get(Number(id)) || null
}

/** @deprecated use getCreatedByLabel */
export function getUploadedByLabel(file, resolveUserNameById) {
  return getCreatedByLabel(file, resolveUserNameById)
}

/**
 * The "Consent" information type (id 28) is a retired field — it was dropped
 * from the upload form (see NewFileForm's own `label !== 'consent'` filter),
 * and the current consent mechanism is `file.privacy_consent_given` plus the
 * file_privacy_consents attestation table. Old files still carry a stray
 * `Consent: 1` / `Consent: yes` row, which is meaningless to a viewer — hide
 * it wherever a file's information types are listed for display.
 */
export function visibleFileInformationTypes(infoTypes) {
  if (!Array.isArray(infoTypes)) return []
  return infoTypes.filter((info) => {
    if (!info) return false
    if (Number(info.information_type_id) === 28) return false
    // Optional "exact wording" rows are saved as '' when left empty.
    if (
      info.field_type === 'electrical_conditions' &&
      String(info.value == null ? '' : info.value).trim() === ''
    ) {
      return false
    }
    return (
      String(info.label || '')
        .trim()
        .toLowerCase() !== 'consent'
    )
  })
}

/**
 * The electrical licence information types only make sense together on the
 * Electrical Licence template (Licence Type depends on State), so they are not
 * offered in the free-form "pick your own information types" lists.
 */
export const ELECTRICAL_FIELD_TYPES = [
  'electrical_licence_type',
  'electrical_restrictions',
  'electrical_conditions',
]
